/**
 * THE EXPERIMENTS EXPLORER AND THE LAB RECEIPT INSPECTOR.
 *
 * WHY THE TWO ARE ONE MODULE. A receipt has no identity of its own in this
 * product: it is reached THROUGH the experiment it belongs to, and the only
 * route that carries one is `GET /gateway/experiments/:experimentId`. So the
 * inspector is a section of the experiment's own page rather than a second
 * surface with its own list — the shape the backend forces, not a preference.
 * They share this file for the same reason the three scientific views share
 * `science.tsx`: the sentence "a receipt is not a result" must not exist in two
 * copies that can drift.
 *
 * ---------------------------------------------------------------------------
 * THE RULES THIS SURFACE IS BUILT ON
 * ---------------------------------------------------------------------------
 *
 *   1. A PLAN IS NOT AN EXECUTION. `PROPOSED` and `QUEUED` are rendered as NOT
 *      RUN. An experiment that was designed, authorized and queued but never
 *      claimed by a lab has produced no measurement, and a view that showed it
 *      beside a completed one without saying so would be presenting intent as
 *      evidence.
 *
 *   2. A PROCESS EXIT IS NOT A RESULT. `exitCode` is what the program returned;
 *      `verdict` is what the evidence did to the hypothesis. They are rendered
 *      as two separate facts in two separate panels, and NOTHING here derives
 *      one from the other. Exit 0 with no verdict is exactly as common in this
 *      record as exit 0 with `SUPPORTED`, and the surface must not imply
 *      otherwise.
 *
 *   3. A MISSING RECEIPT IS NOT A SUCCESS. Where the runner never returned one,
 *      the view says MISSING — it does not render an empty receipt panel that
 *      reads as "nothing went wrong".
 *
 *   4. AN ABSENT FIELD AND AN UNREADABLE ONE ARE DIFFERENT FACTS. The backend
 *      collapses both to `null` in the parsed columns, so this view reads the
 *      `*State` companions the projection now carries. Where the projection
 *      cannot answer, the view says THAT rather than guessing.
 *
 *   5. NOTHING HERE RUNS A MODEL AND NOTHING HERE STARTS ANYTHING. Every read
 *      is a GET. The four routes that propose, probe, run and evaluate exist —
 *      they belong to the Owner, not to this page.
 */

import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';
import { createLoadable, expectsMembers } from '../resource';
import { readGateway, type GatewayError } from '../gateway';
import { Failure } from './failure';
import { DiscussLink } from './discuss';
import { route, navigate } from '../router';
import { PageHeader, Technical } from './about';
import { formatTurnTime as formatRecordTime } from '../chat';

/** HOW MANY THE EXPLORER ASKS FOR. Named here because the view must SAY what it
 *  asked for — a bound a view cannot state is a bound it cannot be honest
 *  about. */
const PAGE = { experiments: 40 } as const;

/** THE SECTION NAMES THAT APPEAR IN A PATH. `/experiments` is the explorer;
 *  `/experiments/experiment/<id>` is one experiment; `/experiments/receipt/<id>`
 *  is its receipt inspector. Two explicit sections rather than a bare id, so a
 *  path is never ambiguous about which surface it names. */
const SECTION = { experiment: 'experiment', receipt: 'receipt' } as const;

/** WHAT THE PROJECTION SAYS ABOUT ONE STORED JSON COLUMN. Three values, because
 *  "nothing was recorded" and "something was recorded and cannot be read" are
 *  not the same fact — see `storedJsonState` in the gateway controller. */
type StoredState = 'ABSENT' | 'UNREADABLE' | 'PRESENT';

/** ONE EXPERIMENT, exactly as `experimentView` projects it. Nothing here is
 *  invented: every field is a key the controller writes, and every optional one
 *  is optional because the column is nullable. */
interface ExperimentRecord {
	experimentId: string;
	missionId: string;
	jobId: string | null;
	hypothesisId: string | null;
	question: string;
	status: string;
	verdict: string | null;
	prediction: string;
	expectedObservation: string;
	falsifier: string | null;
	specHash: string | null;
	spec: unknown;
	environment: unknown;
	receipt: unknown;
	evaluation: unknown;
	specState: StoredState;
	environmentState: StoredState;
	receiptState: StoredState;
	evaluationState: StoredState;
	createdAt: string;
	updatedAt: string;
}

interface JobView {
	jobId: string;
	state: string;
	attempt: number;
	claimedBy: string | null;
	refusalReason: string | null;
	receiptState: 'ABSENT' | 'UNREADABLE' | 'PRESENT';
	receipt: { exitCode: number | null; durationMs: number | null; stdoutCut: boolean } | null;
	enqueuedAt: number;
	lateArtifacts: Array<{ attempt: number | null; disposition: string; agentId: string | null; receivedAt: number }>;
}

interface JobsPayload {
	experimentId: string;
	jobs: JobView[];
	shown: number;
	held: number;
	truncated: boolean;
}

/* ------------------------------------------------------- the state reading */

/**
 * HOW FAR THIS EXPERIMENT ACTUALLY GOT — read off the record, never inferred.
 *
 * The vocabulary is this view's; every value is derived from the backend's own
 * `status` and `verdict` columns, and the mapping is stated here rather than
 * scattered through the rendering so a reader can check it in one place:
 *
 *   PROPOSED            -> NOT RUN   designed; no job has been dispatched
 *   QUEUED              -> NOT RUN   authorized and queued; no lab has run it
 *   RUNNING             -> EXECUTED  a lab returned a receipt; no verdict yet
 *   COMPLETED           -> EXECUTED
 *   FAILED + BLOCKED    -> BLOCKED   the runner refused it, or the spec did not match
 *   FAILED (otherwise)  -> FAILED
 *
 * AN UNRECOGNISED STATUS IS NOT GUESSED AT. A status this build does not carry
 * is reported as itself, because a view that mapped an unknown state onto a
 * known one would be inventing a fact about the record.
 */
interface ExecutionReading {
	label: 'EXECUTED' | 'FAILED' | 'BLOCKED' | 'NOT RUN' | 'RECEIPT MISSING' | 'RECEIPT UNREADABLE' | 'PARTIALLY VERIFIED' | 'UNRECOGNISED';
	/** Why that label, in the record's own terms. */
	because: string;
	tag: string;
}

function readExecution(record: ExperimentRecord): ExecutionReading {
	const receipt = record.receipt;

	// THE RECEIPT OVERLAYS FIRST, BECAUSE IT IS THE STRONGER FACT. An experiment
	// whose status says it ran, but whose receipt is absent or unreadable, is
	// not "executed" in any sense a reader can check.
	if (record.status === 'COMPLETED' || record.status === 'RUNNING') {
		if (record.receiptState === 'ABSENT') {
			return {
				label: 'RECEIPT MISSING',
				because: `the record says ${record.status} and carries NO receipt, so what the sandbox did is not in this record at all`,
				tag: 'tag-warn',
			};
		}
		if (record.receiptState === 'UNREADABLE') {
			return {
				label: 'RECEIPT UNREADABLE',
				because: 'a receipt is stored and this build cannot parse it, so its contents cannot be reported',
				tag: 'tag-warn',
			};
		}
		// EXECUTION HAPPENED — but HOW MUCH of it the receipt can attest to is a
		// second question, and this record set contains both answers. A receipt
		// written by an earlier runner carries no `trials` and no `network`; one
		// from the current runner carries both. Calling the first "EXECUTED" and
		// stopping there would report a partial account as a whole one, so the
		// label says which it is and the inspector lists what is missing.
		const gaps = receiptGaps(receipt);
		if (gaps.length > 0) {
			return {
				label: 'PARTIALLY VERIFIED',
				because: `status ${record.status} with a readable receipt that does not carry ${gaps.join(', ')} — the execution is recorded and only part of it can be checked from here`,
				tag: 'tag-warn',
			};
		}
		return {
			label: 'EXECUTED',
			because: `status ${record.status} with a stored receipt`,
			tag: 'tag-live',
		};
	}
	if (record.status === 'PROPOSED') {
		return {
			label: 'NOT RUN',
			because: 'the experiment is designed and PROPOSED; no job has been dispatched, so there is nothing to measure',
			tag: '',
		};
	}
	if (record.status === 'QUEUED') {
		return {
			label: 'NOT RUN',
			because: 'the job is authorized and QUEUED and no lab has claimed it; a queued job has produced no measurement',
			tag: '',
		};
	}
	if (record.status === 'FAILED') {
		return record.verdict === 'BLOCKED'
			? {
					label: 'BLOCKED',
					because: 'the run was refused or its spec did not match what was authorized, so no measurement was taken',
					tag: 'tag-warn',
				}
			: {
					label: 'FAILED',
					because: 'the run reached the sandbox and did not complete cleanly',
					tag: 'tag-warn',
				};
	}
	return {
		label: 'UNRECOGNISED',
		because: `this build does not carry the status "${record.status}", and it will not map an unknown state onto a known one`,
		tag: 'tag-warn',
	};
}

/** A receipt that is present but does not carry the members a reader would need.
 *  This is a REAL state in this record set: receipts written before the trials
 *  field existed carry no `trials`, and every NONE-mode receipt written before
 *  the network evidence existed carries no `network`. */
function receiptGaps(receipt: unknown): string[] {
	if (typeof receipt !== 'object' || receipt === null || Array.isArray(receipt)) {
		return ['the stored receipt is not an object'];
	}
	const held = receipt as Record<string, unknown>;
	const gaps: string[] = [];
	if (typeof held.exitCode !== 'number') gaps.push('exitCode');
	if (typeof held.durationMs !== 'number') gaps.push('durationMs');
	if (!Array.isArray(held.trials)) gaps.push('trials');
	if (held.network === undefined) gaps.push('network');
	return gaps;
}

/* --------------------------------------------------------------- the pieces */

/** WHAT A QUOTATION MAY COST THE PAGE. A receipt is bounded by the runner and a
 *  spec by the design bounds, but "bounded" is not "small" — a 256 KiB stdout
 *  rendered whole is a page nobody can scroll. So each quotation is cut at a
 *  stated length, and the cut is SAID rather than silent: a reader must be able
 *  to tell an excerpt from the whole of a record. */
const EXCERPT = { spec: 4_000, evaluation: 4_000, environment: 1_000, output: 4_000 } as const;

function Excerpt({ what, text, max }: { what: string; text: string; max: number }) {
	const cut = text.length > max;
	return (
		<>
			<pre class="excerpt">{cut ? text.slice(0, max) : text}</pre>
			{cut && (
				<p class="muted small">
					cut at {max.toLocaleString()} of {text.length.toLocaleString()} characters of {what} — the
					record holds the whole of it and this page does not render the whole of it.
				</p>
			)}
		</>
	);
}

function Bounded({ shown, noun }: { shown: number; noun: string }) {
	return (
		<p class="countline small">
			Showing <span class="count">{shown}</span> {noun}
			<span class="muted">
				{' '}
				— the newest this read asked for. A full page is followed by Load more where the route
				provides a continuation; the count is this page's, not the corpus's.
			</span>
		</p>
	);
}

/** WHAT THE PROJECTION COULD AND COULD NOT READ. Rendered wherever a stored JSON
 *  column is shown, so a reader is never left to infer parse failure from an
 *  empty panel. */
function StoredNote({ what, state }: { what: string; state: StoredState }) {
	if (state === 'PRESENT') return null;
	return (
		<p class="small">
			<span class={`tag${state === 'UNREADABLE' ? ' tag-warn' : ''}`}>
				{state === 'UNREADABLE' ? 'UNREADABLE' : 'NOT RECORDED'}
			</span>{' '}
			<span class="muted">
				{state === 'UNREADABLE'
					? `${what} is stored and this build cannot parse it — the bytes exist and cannot be reported. This is a defect in the record, not an absence of one.`
					: `no ${what} was recorded for this experiment.`}
			</span>
		</p>
	);
}

/** One experiment, as a row in the explorer. */
function ExperimentRow({ record }: { record: ExperimentRecord }) {
	const reading = readExecution(record);
	return (
		<li class="record">
			<div class="record-head">
				<code>{record.experimentId}</code>
				<span class={`tag${reading.tag === '' ? '' : ` ${reading.tag}`}`}>{reading.label}</span>
				{record.verdict !== null && <span class="tag">verdict {record.verdict}</span>}
				<span class="tag">status {record.status}</span>
			</div>
			<p class="record-summary">{record.question}</p>
			{/* THE REASON, NOT ONLY THE LABEL. A state chip on its own is a claim
			    without its basis: "RECEIPT MISSING" and "NOT RUN" are both
			    absences to a reader who is not told which absence they are
			    looking at. */}
			<p class="muted small">{reading.because}</p>
			<p class="muted small">
				opened <time dateTime={record.createdAt}>{record.createdAt}</time> · updated{' '}
				<time dateTime={record.updatedAt}>{record.updatedAt}</time>
				{record.hypothesisId !== null && <> · hypothesis <code>{record.hypothesisId}</code></>}
			</p>
			<p class="small">
				<a
					class="button ghost small"
					href={`/research/experiments/${encodeURIComponent(record.experimentId)}`}
					onClick={(event) => {
						if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
						event.preventDefault();
						navigate({ destination: 'research', section: 'experiments', recordId: record.experimentId });
					}}
				>
					Open
				</a>{' '}
				{/* THE CANONICAL LABEL, NOT A SHORTENED ONE. "Discuss with Scout" is
				    the product's own name for this action and the browser instrument
				    asserts a view holding records offers it — a shortened label
				    would read as a different affordance. */}
				<DiscussLink ref={`experiment:${record.experimentId}`} />
			</p>
		</li>
	);
}

/** THE EXPLORER. */
function ExperimentExplorer() {
	const [older, setOlder] = useState<ExperimentRecord[]>([]);
	const [more, setMore] = useState(false);
	const [paging, setPaging] = useState(false);
	const [pageError, setPageError] = useState<GatewayError | null>(null);
	const experiments = useMemo(
		() =>
			createLoadable<{ experiments: ExperimentRecord[]; hasMore?: boolean }>(
				() => readGateway(`/api/scout/gateway/experiments?limit=${PAGE.experiments}`),
				expectsMembers('experiments'),
			),
		[],
	);
	const lab = useMemo(
		() => createLoadable<{ agent?: { presence?: string }; availability?: string }>(
			() => readGateway('/api/scout/gateway/lab/status'),
			expectsMembers('availability'),
		),
		[],
	);
	useEffect(() => {
		setOlder([]);
		setMore(false);
		setPageError(null);
		void experiments.load();
		void lab.load();
	}, [experiments, lab]);

	const listState = experiments.state.value;
	const labState = lab.state.value;
	const records = [...(listState.data?.experiments ?? []), ...older];
	const hasMore =
		more || (older.length === 0 && listState.data?.hasMore === true);
	const loadMore = useCallback(async () => {
		if (listState.status !== 'ready' || listState.data === null) return;
		setPaging(true);
		setPageError(null);
		const offset = listState.data.experiments.length + older.length;
		const result = await readGateway<{ experiments: ExperimentRecord[]; hasMore?: boolean }>(
			`/api/scout/gateway/experiments?limit=${PAGE.experiments}&offset=${offset}`,
		);
		setPaging(false);
		if (!result.ok) {
			setPageError(result.error);
			return;
		}
		setOlder((previous) => [...previous, ...result.data.experiments]);
		setMore(result.data.hasMore === true);
	}, [listState, older]);

	return (
		<section class="view" aria-labelledby="experiments-heading">
			<PageHeader
				id="experiments-heading"
				title="Experiments"
				about={{
					purpose:
						'What Scout proposed to test, the authority it ran under, what the sandbox actually did, and what the result did to the hypothesis. A plan and an execution are rendered differently, because they are different things.',
					how: (
						<p>
							Open a record to see whether it actually ran, what was proposed before anything ran, and
							what the result changed. A receipt is a separate record: it is evidence about the
							process, never a scientific result.
						</p>
					),
					limits: (
						<>
							<p>
								<strong>A receipt is not proof that a container ran, and not a verdict.</strong> The
								limits a run was given are recorded; what the container actually consumed is not.
								There is no artefact store in this path.
							</p>
							<p>
								<strong>Every list is a page.</strong> This view cannot say how many experiments
								exist beyond the page it reads.
							</p>
						</>
					),
				}}
			/>

			{/* WHETHER EXECUTION IS EVEN POSSIBLE RIGHT NOW. A queued experiment is
			    waiting for a lab agent, and a list that said "queued" without
			    saying whether anything is polling would describe a wait that is
			    not happening. */}
			<section class="panel" aria-labelledby="experiments-lab">
				<h2 id="experiments-lab">The lab this would run in</h2>
				{labState.status === 'loading' && (
					<p class="notice" role="status">
						Asking whether a lab agent is present…
					</p>
				)}
				{labState.status === 'failed' && labState.error !== null && (
					<Failure error={labState.error} what="the lab's presence" onRetry={() => void lab.load()} />
				)}
				{labState.status === 'ready' && labState.data !== null && (
					<p>
						<span class="tag">{labPresenceLabel(labState.data.agent?.presence)}</span>{' '}
						<span class="muted">{labState.data.availability ?? 'the route gave no reason'}</span>
					</p>
				)}
			</section>

			<section class="panel" aria-labelledby="experiments-list">
				<h2 id="experiments-list">Proposed and run</h2>
				{listState.status === 'loading' && (
					<p class="notice" role="status">
						Reading Scout’s experiments…
					</p>
				)}
				{listState.status === 'failed' && listState.error !== null && (
					<Failure error={listState.error} what="the experiments" onRetry={() => void experiments.load()} />
				)}
				{listState.status === 'ready' && listState.data !== null && (
					<>
						{records.length === 0 ? (
							<p>
								<strong>No experiment is on record.</strong>{' '}
								<span class="muted">
									An experiment is opened when the Owner proposes one against an authorized mission,
									or when a probe is run. An empty list here means none has been opened, not that one
									is hidden.
								</span>
							</p>
						) : (
							<>
								<Bounded shown={records.length} noun="experiments" />
								{/* THE INDEX IS THE SCAN; the records below keep the full
								    protocol and receipt. Selecting a row opens the experiment's
								    own address rather than an in-page expansion, because its
								    detail is a page with its own jobs read. */}
								<div class="table-wrap">
									<table class="data-table experiment-index">
										<thead>
											<tr>
												<th scope="col">Experiment</th>
												<th scope="col">Status</th>
												<th scope="col">Verdict</th>
												<th scope="col">Opened</th>
											</tr>
										</thead>
										<tbody>
											{records.map((record) => {
												const href = `/research/experiments/${encodeURIComponent(record.experimentId)}`;
												return (
													<tr
														key={`index:${record.experimentId}`}
														class="row-link"
														onClick={(event) => {
															if ((event.target as HTMLElement).closest('a') !== null) return;
															navigate({
																destination: 'research',
																section: 'experiments',
																recordId: record.experimentId,
															});
														}}
													>
														<td class="cell-title" data-label="Experiment">
															<a
																href={href}
																onClick={(event) => {
																	if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
																	event.preventDefault();
																	navigate({
																		destination: 'research',
																		section: 'experiments',
																		recordId: record.experimentId,
																	});
																}}
															>
																{record.question}
															</a>
															<span class="cell-sub">{record.experimentId}</span>
														</td>
														<td data-label="Status">
															<span class={`status status-${record.status.toLowerCase()}`}>
																{record.status}
															</span>
														</td>
														<td data-label="Verdict">{record.verdict ?? '—'}</td>
														<td class="cell-date" data-label="Opened">
															{formatRecordTime(record.createdAt)}
														</td>
													</tr>
												);
											})}
										</tbody>
									</table>
								</div>
								<ul class="records">
									{records.map((record) => (
										<ExperimentRow key={record.experimentId} record={record} />
									))}
								</ul>
								{hasMore && (
									<button
										type="button"
										class="button ghost"
										disabled={paging}
										onClick={() => void loadMore()}
									>
										{paging ? 'Reading the next page…' : 'Load more experiments'}
									</button>
								)}
								{pageError !== null && (
									<p class="notice small" role="status">
										The next page of experiments could not be read: {pageError.message}
									</p>
								)}
							</>
						)}
					</>
				)}
			</section>
		</section>
	);
}

/** ONE EXPERIMENT, IN FULL — the specification, the authority, and the receipt. */
function ExperimentDetail({ experimentId }: { experimentId: string }) {
	const detail = useMemo(
		() =>
			createLoadable<ExperimentRecord>(
				() => readGateway(`/api/scout/gateway/experiments/${encodeURIComponent(experimentId)}`),
				expectsMembers('experimentId', 'status', 'receiptState'),
			),
		[experimentId],
	);
	useEffect(() => {
		void detail.load();
	}, [detail]);
	const state = detail.state.value;

	const back = (
		<p class="small">
			<a
				class="button ghost small"
				href="/experiments"
				onClick={(event) => {
					if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
					event.preventDefault();
					navigate({ destination: 'research', section: 'experiments', recordId: null });
				}}
			>
				All experiments
			</a>
		</p>
	);

	if (state.status === 'loading') {
		return (
			<section class="view">
				<p class="notice" role="status">
					Reading the experiment…
				</p>
			</section>
		);
	}
	if (state.status === 'failed' && state.error !== null) {
		return (
			<section class="view">
				<Failure error={state.error} what="this experiment" onRetry={() => void detail.load()} />
				{back}
			</section>
		);
	}
	if (state.data === null) return null;

	const record = state.data;
	const reading = readExecution(record);

	return (
		<section class="view" aria-labelledby="experiment-heading">
			<header class="view-head">
				<h1 id="experiment-heading">{record.question}</h1>
				<p class="view-blurb">
					<code>{record.experimentId}</code> · <span class="tag">{reading.label}</span>
					{record.verdict !== null && <span class="tag"> verdict {record.verdict}</span>}
				</p>
			</header>

			<section class="panel" aria-labelledby="experiment-execution">
				<h2 id="experiment-execution">Whether it ran</h2>
				<p>{reading.because}</p>
				<dl class="facts">
					<dt>status</dt>
					<dd>
						<code>{record.status}</code>
					</dd>
					<dt>verdict</dt>
					<dd>
						{record.verdict === null ? (
							<span class="muted">
								none is recorded — the evidence has not been weighed against the hypothesis, which is
								not the same as the hypothesis surviving
							</span>
						) : (
							<code>{record.verdict}</code>
						)}
					</dd>
					<dt>job</dt>
					<dd>
						{record.jobId === null ? (
							<span class="muted">no job was dispatched</span>
						) : (
							<code>{record.jobId}</code>
						)}
					</dd>
					<dt>mission</dt>
					<dd>
						<code>{record.missionId}</code>{' '}
						<span class="muted">— the authority this ran under</span>
					</dd>
					<dt>hypothesis</dt>
					<dd>
						{record.hypothesisId === null ? (
							<span class="muted">
								none is linked — the design was exploratory, so no stored hypothesis was under test
							</span>
						) : (
							<code>{record.hypothesisId}</code>
						)}
					</dd>
					<dt>spec hash</dt>
					<dd>
						{record.specHash === null ? (
							<span class="muted">not computed — nothing was dispatched</span>
						) : (
							<code>{record.specHash}</code>
						)}
					</dd>
				</dl>
				<p class="small">
					<DiscussLink ref={`experiment:${record.experimentId}`} label="Discuss this experiment" />{' '}
					<a
						class="button ghost small"
						href={`/research/experiments/${encodeURIComponent(`${SECTION.receipt}:${record.experimentId}`)}`}
						onClick={(event) => {
							if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
							event.preventDefault();
							navigate({ destination: 'research', section: 'experiments', recordId: `${SECTION.receipt}:${record.experimentId}` });
						}}
					>
						Open the receipt
					</a>
				</p>
			</section>

			<section class="panel" aria-labelledby="experiment-design">
				<h2 id="experiment-design">What was proposed, before anything ran</h2>
				<dl class="facts">
					<dt>prediction</dt>
					<dd>{record.prediction}</dd>
					<dt>expected observation</dt>
					<dd>{record.expectedObservation}</dd>
					<dt>falsifier</dt>
					<dd>
						{record.falsifier === null || record.falsifier.trim().length === 0 ? (
							<span class="muted">none was declared — nothing stated in advance what would disprove it</span>
						) : (
							record.falsifier
						)}
					</dd>
				</dl>
				<StoredNote what="the program specification" state={record.specState} />
				{record.specState === 'PRESENT' && (
					<Excerpt what="the specification" text={JSON.stringify(record.spec, null, 2)} max={EXCERPT.spec} />
				)}
			</section>

			<section class="panel" aria-labelledby="experiment-interpretation">
				<h2 id="experiment-interpretation">What the result did to the hypothesis</h2>
				<StoredNote what="an evaluation" state={record.evaluationState} />
				{record.evaluationState === 'PRESENT' ? (
					<Excerpt what="the evaluation" text={JSON.stringify(record.evaluation, null, 2)} max={EXCERPT.evaluation} />
				) : (
					<p class="muted small">
						Without an evaluation this experiment has a RESULT but no INTERPRETATION: the sandbox
						measured something and nothing has yet said what it means for the hypothesis.
					</p>
				)}
			</section>

			{back}
		</section>
	);
}

/** THE LAB RECEIPT INSPECTOR. */
function ReceiptInspector({ experimentId }: { experimentId: string }) {
	const detail = useMemo(
		() =>
			createLoadable<ExperimentRecord>(
				() => readGateway(`/api/scout/gateway/experiments/${encodeURIComponent(experimentId)}`),
				expectsMembers('experimentId', 'status', 'receiptState'),
			),
		[experimentId],
	);
	useEffect(() => {
		void detail.load();
	}, [detail]);
	const jobs = useMemo(
		() =>
			createLoadable<JobsPayload>(
				() => readGateway(`/api/scout/gateway/experiments/${encodeURIComponent(experimentId)}/jobs`),
				expectsMembers('jobs', 'shown', 'held'),
			),
		[experimentId],
	);
	useEffect(() => {
		void jobs.load();
	}, [jobs]);
	const state = detail.state.value;
	const jobsState = jobs.state.value;

	if (state.status === 'loading') {
		return (
			<section class="view">
				<p class="notice" role="status">
					Reading the receipt…
				</p>
			</section>
		);
	}
	if (state.status === 'failed' && state.error !== null) {
		return (
			<section class="view">
				<Failure error={state.error} what="this receipt" onRetry={() => void detail.load()} />
			</section>
		);
	}
	if (state.data === null) return null;

	const record = state.data;
	const reading = readExecution(record);
	const receipt =
		typeof record.receipt === 'object' && record.receipt !== null && !Array.isArray(record.receipt)
			? (record.receipt as Record<string, unknown>)
			: null;
	const gaps = receipt === null ? [] : receiptGaps(record.receipt);

	return (
		<section class="view" aria-labelledby="receipt-heading">
			<header class="view-head">
				<h1 id="receipt-heading">Lab receipt</h1>
				<p class="view-blurb">
					What the sandbox returned for <code>{record.experimentId}</code>. A receipt is the runner's own
					account of one execution. It is not proof that a container ran, and it is not a scientific
					result.
				</p>
			</header>

			<section class="panel" aria-labelledby="receipt-state">
				<h2 id="receipt-state">Is there one</h2>
				<p>
					<span class={`tag${reading.tag === '' ? '' : ` ${reading.tag}`}`}>{reading.label}</span>{' '}
					{reading.because}
				</p>
				{record.receiptState !== 'PRESENT' && (
					<p class="muted small">
						Nothing below can be reported, because there is no readable receipt to report from. This is
						stated rather than left as an empty panel, which would read as an execution that went fine.
					</p>
				)}
			</section>

			{receipt !== null && (
				<>
					{/* IDENTITY — what this receipt is a receipt FOR. */}
					<section class="panel" aria-labelledby="receipt-identity">
						<h2 id="receipt-identity">What this is a receipt for</h2>
						<dl class="facts">
							<dt>experiment</dt>
							<dd>
								<code>{record.experimentId}</code>
							</dd>
							<dt>job</dt>
							<dd>
								{record.jobId === null ? <span class="muted">none recorded</span> : <code>{record.jobId}</code>}
							</dd>
							<dt>attempt</dt>
							<dd>
								{typeof receipt.attempt === 'number' ? (
									<code>{String(receipt.attempt)}</code>
								) : (
									<span class="muted">not carried on this receipt</span>
								)}
							</dd>
							<dt>spec hash</dt>
							<dd>
								{record.specHash === null ? (
									<span class="muted">not recorded</span>
								) : (
									<code>{record.specHash}</code>
								)}{' '}
								<span class="muted">
									— the revision of the program this receipt answers; the runner checks the receipt
									against it rather than trusting either alone
								</span>
							</dd>
							<dt>executor</dt>
							<dd>
								<span class="muted">
									Named per attempt in <strong>The attempts</strong> below, read from the job rows. An
									executor is never inferred here: a job nobody claimed says so rather than being
									attributed to whichever agent was last seen.
								</span>
							</dd>
						</dl>
					</section>

					{/* EXECUTION — what the process did. */}
					<section class="panel" aria-labelledby="receipt-execution">
						<h2 id="receipt-execution">What the process did</h2>
						<dl class="facts">
							<dt>exit status</dt>
							<dd>
								{typeof receipt.exitCode === 'number' ? (
									<>
										<code>{String(receipt.exitCode)}</code>
										{receipt.timedOut === true && <span class="tag tag-warn"> timed out</span>}{' '}
										<span class="muted">
											— what the program returned. It says nothing about whether the hypothesis was
											supported.
										</span>
									</>
								) : (
									<span class="muted">not carried on this receipt</span>
								)}
							</dd>
							<dt>duration</dt>
							<dd>
								{typeof receipt.durationMs === 'number' ? (
									<code>{String(receipt.durationMs)}ms</code>
								) : (
									<span class="muted">not carried on this receipt</span>
								)}
							</dd>
							<dt>started</dt>
							<dd>
								{typeof receipt.startedAt === 'number' ? (
									<time dateTime={new Date(receipt.startedAt).toISOString()}>
										{new Date(receipt.startedAt).toISOString()}
									</time>
								) : (
									<span class="muted">not carried on this receipt</span>
								)}
							</dd>
							<dt>finished</dt>
							<dd>
								{typeof receipt.finishedAt === 'number' ? (
									<time dateTime={new Date(receipt.finishedAt).toISOString()}>
										{new Date(receipt.finishedAt).toISOString()}
									</time>
								) : (
									<span class="muted">not carried on this receipt</span>
								)}
							</dd>
							<dt>trials</dt>
							<dd>
								{Array.isArray(receipt.trials) ? (
									<code>{String(receipt.trials.length)}</code>
								) : (
									<span class="muted">
										NOT RECORDED — this receipt was written before per-trial identity existed, so the
										top-level readings above are all this build can attribute. Whether a control ran
										cannot be answered from it.
									</span>
								)}
							</dd>
						</dl>
					</section>

					{/* ENVIRONMENT — where it ran, as far as the record says. */}
					<section class="panel" aria-labelledby="receipt-environment">
						<h2 id="receipt-environment">Where it ran</h2>
						{record.environmentState === 'PRESENT' ? (
							<Excerpt what="the environment" text={JSON.stringify(record.environment, null, 2)} max={EXCERPT.environment} />
						) : (
							<StoredNote what="the execution environment" state={record.environmentState} />
						)}
						<p class="small">
							<strong>network:</strong>{' '}
							{receipt.network === undefined ? (
								<span class="muted">
									NOT RECORDED on this receipt. The runner records the broker's decisions only for a
									PUBLIC run; on a NONE run the field is absent, and a receipt written before the field
									existed is absent for both reasons. This view cannot tell those two apart and will not
									guess.
								</span>
							) : (
								<code>{JSON.stringify(receipt.network)}</code>
							)}
						</p>
					</section>

					{/* OUTPUT — bounded, with the digests that identify it. */}
					<section class="panel" aria-labelledby="receipt-output">
						<h2 id="receipt-output">What it printed</h2>
						{typeof receipt.stdout === 'string' && receipt.stdout.length > 0 ? (
							<>
								<p class="small muted">
									stdout{receipt.stdoutTruncated === true ? ' (TRUNCATED by the runner)' : ''} ·{' '}
									{typeof receipt.stdoutDigest === 'string' ? <code>{receipt.stdoutDigest}</code> : 'no digest'}
								</p>
								<Excerpt what="stdout" text={receipt.stdout} max={EXCERPT.output} />
							</>
						) : (
							<p class="muted small">the receipt carries no stdout</p>
						)}
						{typeof receipt.stderr === 'string' && receipt.stderr.length > 0 ? (
							<>
								<p class="small muted">
									stderr{receipt.stderrTruncated === true ? ' (TRUNCATED by the runner)' : ''} ·{' '}
									{typeof receipt.stderrDigest === 'string' ? <code>{receipt.stderrDigest}</code> : 'no digest'}
								</p>
								<Excerpt what="stderr" text={receipt.stderr} max={EXCERPT.output} />
							</>
						) : (
							<p class="muted small">the receipt carries no stderr</p>
						)}
					</section>

					{/* WHAT IS NOT HERE. The gaps are derived from the receipt itself, so
					    this list is a reading of the record rather than a standing
					    disclaimer. */}
					{gaps.length > 0 && (
						<section class="panel not-known" aria-labelledby="receipt-gaps">
							<h2 id="receipt-gaps">What this receipt does not carry</h2>
							<ul>
								{gaps.map((gap) => (
									<li key={gap}>
										<code>{gap}</code>
									</li>
								))}
							</ul>
							<p class="muted small">
								Each of these is a field a reader might expect and this particular receipt does not
								hold. The receipt is not incomplete in the sense of being corrupt — it was written by an
								earlier version of the runner than the one this build reads.
							</p>
						</section>
					)}
				</>
			)}

			{/* THE ATTEMPTS, AND THE AUTHORITATIVE RECEIPT EACH ONE PRODUCED.
			    A job row is where the claim was written, so this is the only place an
			    executor can be NAMED rather than guessed — and the only place a late
			    arrival is visible, since a submission that missed its window is
			    preserved beside the job rather than accepted as its result. */}
			<section class="panel" aria-labelledby="receipt-attempts">
				<h2 id="receipt-attempts">The attempts</h2>
				{jobsState.status === 'loading' && (
					<p class="notice" role="status">
						Reading the attempts…
					</p>
				)}
				{jobsState.status === 'failed' && jobsState.error !== null && (
					<Failure error={jobsState.error} what="the attempts" onRetry={() => void jobs.load()} />
				)}
				{jobsState.status === 'ready' && jobsState.data !== null && (
					<>
						{jobsState.data.jobs.length === 0 ? (
							<p>
								<strong>No job was ever dispatched for this experiment.</strong>{' '}
								<span class="muted">
									An experiment that was designed and authorized still produces no measurement
									until a lab agent claims a job, so nothing here ran.
								</span>
							</p>
						) : (
							<>
								<p class="countline small">
									Showing <span class="count">{jobsState.data.shown}</span> of{' '}
									<span class="count">{jobsState.data.held}</span> attempt(s) on record
									{jobsState.data.truncated && <span class="muted"> — this read was cut short</span>}
								</p>
								<ul class="records">
									{jobsState.data.jobs.map((job) => (
										<li key={job.jobId} class="record">
											<div class="record-head">
												<code>{job.jobId}</code>
												<span class="tag">{job.state}</span>
												<span class="tag">attempt {job.attempt}</span>
												{job.refusalReason !== null && (
													<span class="tag tag-warn">refused {job.refusalReason}</span>
												)}
											</div>
											<p class="small">
												<strong>executor:</strong>{' '}
												{job.claimedBy === null ? (
													<span class="muted">
														none — no agent claimed this job, so no executor is recorded. A job
														that waited is not a job that ran.
													</span>
												) : (
													<code>{job.claimedBy}</code>
												)}
											</p>
											<p class="small">
												<strong>receipt:</strong> <span class="tag">{job.receiptState}</span>
												{job.receipt !== null && job.receipt.exitCode !== null && (
													<>
														{' '}
														<span class="muted">
															exit {job.receipt.exitCode}
															{job.receipt.durationMs !== null && ` · ${job.receipt.durationMs}ms`}
															{job.receipt.stdoutCut && ' · stdout cut for display'}
														</span>
													</>
												)}
											</p>
											<p class="muted small">
												enqueued{' '}
												<time dateTime={new Date(job.enqueuedAt).toISOString()}>
													{new Date(job.enqueuedAt).toISOString()}
												</time>
											</p>
											{job.lateArtifacts.length > 0 && (
												<p class="small">
													<span class="tag tag-warn">
														{job.lateArtifacts.length} late arrival(s)
													</span>{' '}
													<span class="muted">
														preserved beside the job and never accepted as its result — a
														submission that missed its window is evidence of an attempt, not an
														answer
													</span>
												</p>
											)}
										</li>
									))}
								</ul>
							</>
						)}
					</>
				)}
			</section>

			<p class="small">
				<a
					class="button ghost small"
					href={`/research/experiments/${encodeURIComponent(record.experimentId)}`}
					onClick={(event) => {
						if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
						event.preventDefault();
						navigate({ destination: 'research', section: 'experiments', recordId: record.experimentId });
					}}
				>
					The experiment itself
				</a>
			</p>
		</section>
	);
}

/**
 * WHAT THE LAB'S PRESENCE TAG SAYS — the state names are the backend's, and
 * one of them is longer than a tag should be. `CLOUD_AVAILABLE` means the
 * deployment's own executor is bound (not a heartbeat); the label says the lab
 * is ready without implying an agent polled.
 */
function labPresenceLabel(presence: string | undefined): string {
	if (presence === 'CLOUD_AVAILABLE') return 'CLOUD LAB READY';
	return presence ?? 'UNKNOWN';
}

/**
 * THE EXPERIMENTS TAB'S ONE ENTRY POINT. The record id selects the surface:
 * a `receipt:` prefix opens the receipt inspector, any other id opens the
 * experiment itself, and no id opens the explorer. The prefix is part of the
 * address so the grammar stays three segments deep and every surface survives
 * a reload.
 */
export function ExperimentsView() {
	const current = route.value;
	if (current.section !== 'experiments' || current.recordId === null) {
		return <ExperimentExplorer />;
	}
	const receiptPrefix = `${SECTION.receipt}:`;
	if (current.recordId.startsWith(receiptPrefix)) {
		return <ReceiptInspector experimentId={current.recordId.slice(receiptPrefix.length)} />;
	}
	return <ExperimentDetail experimentId={current.recordId} />;
}
